'use client';

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  AiOutlineMenu,
  AiOutlinePushpin,
  AiFillPushpin,
} from 'react-icons/ai';
import {
  MdKeyboardArrowDown,
  MdSpaceDashboard,
  MdEmojiTransportation,
  MdOutlineProductionQuantityLimits,
} from 'react-icons/md';
import { RiArrowDropRightLine } from 'react-icons/ri';
import { BiPurchaseTag } from 'react-icons/bi';
import {
  FaCog,
  FaTools,
  FaUserPlus,
  FaTruck,
  FaRoute,
  FaChartBar,
  FaChartArea,
  FaFileAlt,
  FaClipboardCheck,
} from 'react-icons/fa';
import { PiGitBranchBold } from 'react-icons/pi';
import { TbMapPinCode } from 'react-icons/tb';
import { LuFileSpreadsheet } from 'react-icons/lu';
import { SidebarContext } from './MainLayout';
import { usePermissions } from '../context/PermissionContext';

/* ────────────────────────────────────────────────
   Reusable NavLink Item (Flat Links)
───────────────────────────────────────────────── */
const NavItem = ({ to, icon: Icon, label, isExpanded, onClick }) => {
  const { can } = usePermissions();
  if (!can(label, 'view')) return null;

  const getClass = ({ isActive }) =>
    `flex items-center gap-3 py-2.5 text-base font-medium rounded-lg transition-all duration-300
     ${isExpanded ? 'px-3 justify-start' : 'px-2 justify-center'}
     ${isActive
      ? 'bg-blue-50 text-blue-700 font-semibold shadow-sm ring-1 ring-blue-100'
      : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
    }`;

  return (
    <NavLink to={to} className={getClass} onClick={onClick} end title={!isExpanded ? label : undefined}>
      <Icon className="text-xl min-w-[20px] opacity-80" />
      {isExpanded && <span>{label}</span>}
    </NavLink>
  );
};

/* ────────────────────────────────────────────────
   Dropdown Item (Nested Links)
───────────────────────────────────────────────── */
const DropdownItem = ({ to, icon: Icon, label, isExpanded, onClick }) => {
  const { can } = usePermissions();
  if (!can(label, 'view')) return null;

  return (
    <NavLink
      to={to}
      onClick={onClick}
      end
      title={!isExpanded ? label : undefined}
      className={({ isActive }) => `flex items-center gap-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${isExpanded ? 'px-3 justify-start' : 'px-2 justify-center'} ${isActive ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'}`}
    >
      <RiArrowDropRightLine className="text-xl opacity-70" />
      <Icon className="text-lg opacity-80" />
      {isExpanded && <span>{label}</span>}
    </NavLink>
  );
};

/* ────────────────────────────────────────────────
   Dropdown Section
───────────────────────────────────────────────── */
const DropdownSection = ({
  section,
  isExpanded,
  openDropdown,
  toggleDropdown,
  closeMobile,
}) => {
  const { can } = usePermissions();
  const { id, title, icon: HeaderIcon, items } = section;
  const isOpen = openDropdown === id && isExpanded;

  const visibleItems = items.filter(it => can(it.label, 'view'));
  if (visibleItems.length === 0) return null;

  return (
    <li>
      <button
        onClick={() => toggleDropdown(id)}
        title={!isExpanded ? title : undefined}
        className={`flex items-center w-full py-2.5 text-base font-medium rounded-lg transition-all duration-300 
          ${isExpanded ? 'px-3 justify-start' : 'px-2 justify-center'}
          ${isOpen
            ? 'bg-blue-50 text-blue-700 font-semibold ring-1 ring-blue-100'
            : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
          }`}
      >
        <HeaderIcon className="text-xl min-w-[20px] opacity-80" />
        {isExpanded && (
          <>
            <span className="flex-1 text-left ml-3">{title}</span>
            <MdKeyboardArrowDown
              className={`text-lg transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
            />
          </>
        )}
      </button>

      <ul
        className={`pl-6 pr-2 mt-1 space-y-1 transition-all duration-300 ease-in-out
          ${isOpen
            ? 'max-h-[400px] overflow-y-auto opacity-100 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent'
            : 'max-h-0 overflow-hidden opacity-0'
          }`}
      >
        {visibleItems.map((item) => (
          <li key={item.to}>
            <DropdownItem
              to={item.to}
              icon={item.icon}
              label={item.label}
              isExpanded={isExpanded}
              onClick={closeMobile}
            />
          </li>
        ))}
      </ul>
    </li>
  );
};

/* ────────────────────────────────────────────────
   Main Sidebar Component
───────────────────────────────────────────────── */
const SideBar = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [openDropdown, setOpenDropdown] = useState('');
  const location = useLocation();
  const { setIsExpanded } = useContext(SidebarContext);
  const { can } = usePermissions();

  const isExpanded = isPinned || isHovered || isMobileOpen;
  const [prevExpanded, setPrevExpanded] = useState(isExpanded);

  useEffect(() => {
    setIsExpanded(isExpanded);
    if (!isExpanded && prevExpanded) setOpenDropdown('');
    setPrevExpanded(isExpanded);
  }, [isExpanded, prevExpanded, setIsExpanded]);

  const toggleDropdown = useCallback((id) => {
    if (!isExpanded) return;
    setOpenDropdown(prev => prev === id ? '' : id);
  }, [isExpanded]);

  const closeMobile = useCallback(() => {
    if (window.innerWidth < 640) setIsMobileOpen(false);
  }, []);

  // ── Unified Menu Order ──
  const navigationItems = [
    { type: 'link', to: '/Dashboard', icon: MdSpaceDashboard, label: 'Dashboard' },
    {
      type: 'dropdown',
      id: 'masters',
      title: 'Masters',
      icon: FaCog,
      items: [
        { to: '/UserMaster', icon: FaUserPlus, label: 'User Master' },
        { to: '/Role', icon: MdEmojiTransportation, label: 'Role Master' },
        { to: '/Customer', icon: FaUserPlus, label: 'Customer Master' },
        { to: '/Routepage', icon: FaRoute, label: 'Route Master' },
        { to: '/Customer-price', icon: MdOutlineProductionQuantityLimits, label: 'Customer Price Setting' },
        { to: '/StateMaster', icon: MdEmojiTransportation, label: 'State Master' },
        { to: '/AreaMasterPage', icon: FaChartArea, label: 'Area Master' },
        { to: '/Pincode', icon: TbMapPinCode, label: 'Pincode Master' },
        { to: '/BranchMaster', icon: PiGitBranchBold, label: 'Branch Master' },
        { to: '/Transport', icon: MdEmojiTransportation, label: 'Transport Master' },
      ],
    },
    { type: 'link', to: '/pickin', icon: FaClipboardCheck, label: 'Pick In' },
    { type: 'link', to: '/booking', icon: FaFileAlt, label: 'Booking' },
    {
      type: 'dropdown',
      id: 'manifest',
      title: 'Manifest',
      icon: FaTruck,
      items: [
        { to: '/OutgoingManifest', icon: FaTruck, label: 'Outgoing Manifest' },
        { to: '/IncomingManifest', icon: FaTruck, label: 'Incoming Manifest' },
      ],
    },
    { type: 'link', to: '/Runsheet', icon: LuFileSpreadsheet, label: 'Runsheet' },
    {
      type: 'dropdown',
      id: 'transaction',
      title: 'Transaction',
      icon: FaTools,
      items: [
        { to: '/docket', icon: BiPurchaseTag, label: 'Docket Confirmation' },
        { to: '/invoice', icon: BiPurchaseTag, label: 'Invoice' },
        { to: '/collection', icon: BiPurchaseTag, label: 'Collection' },
      ],
    },
    {
      type: 'dropdown',
      id: 'reports',
      title: 'Reports',
      icon: FaChartBar,
      items: [
        { to: '/ManifestReport', icon: FaChartBar, label: 'Manifest Report' },
        { to: '/RunsheetReport', icon: FaChartBar, label: 'Runsheet Report' },
        { to: '/DeliveryReport', icon: FaChartBar, label: 'Delivery Report' },
        { to: '/MISReport', icon: FaChartBar, label: 'MIS Report' },
      ],
    },
    { type: 'link', to: '/Tracking', icon: LuFileSpreadsheet, label: 'Tracking No' },
    { type: 'link', to: '/customer-dashboard', icon: FaChartArea, label: 'Customer Dashboard' },
  ];

  // Auto-expand the dropdown if a child link is active
  useEffect(() => {
    if (!isExpanded) return;
    
    const currentPath = location.pathname;
    let activeDropdown = '';
    
    for (const item of navigationItems) {
      if (item.type === 'dropdown') {
        const isChildActive = item.items.some(child => 
          currentPath === child.to || currentPath.startsWith(child.to + '/')
        );
        if (isChildActive) {
          activeDropdown = item.id;
          break;
        }
      }
    }
    
    // Auto-update if there is an active dropdown matching the current route
    if (activeDropdown) {
      setOpenDropdown(activeDropdown);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isExpanded]);

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsMobileOpen(v => !v)}
        className="fixed top-20 left-4 z-50 p-2 rounded-full bg-white text-slate-700 border border-gray-200 shadow-md sm:hidden transition-all hover:scale-110 hover:text-blue-600"
        aria-label="Toggle sidebar"
      >
        <AiOutlineMenu className="w-6 h-6" />
      </button>

      {/* Sidebar Container */}
      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`fixed top-[65px] left-0 z-40 h-full bg-white text-slate-700 border-r border-gray-200 transition-all duration-300 ease-in-out overflow-hidden shadow-sm
          ${isExpanded ? 'w-64' : 'w-16'} 
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}`}
      >
        {/* Pin Button */}
        <div className="absolute top-3 right-3 z-10">
          <button
            onClick={() => setIsPinned(p => !p)}
            className={`p-2 rounded-full transition-all duration-300 shadow-sm border
              ${isPinned
                ? 'bg-blue-50 text-blue-600 border-blue-200'
                : 'bg-white text-gray-400 hover:bg-slate-50 hover:text-blue-600 border-transparent hover:border-gray-200'
              }`}
            title={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
          >
            {isPinned ? <AiFillPushpin className="w-5 h-5" /> : <AiOutlinePushpin className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className={`h-full pt-16 pb-20 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent ${isExpanded ? 'pl-4 pr-2' : 'px-2'}`}>
          <ul className="space-y-1">
            {navigationItems.map((item, index) => {
              if (item.type === 'link') {
                return (
                  <li key={item.to || index}>
                    <NavItem
                      to={item.to}
                      icon={item.icon}
                      label={item.label}
                      isExpanded={isExpanded}
                      onClick={closeMobile}
                    />
                  </li>
                );
              } else {
                return (
                  <DropdownSection
                    key={item.id || index}
                    section={item}
                    isExpanded={isExpanded}
                    openDropdown={openDropdown}
                    toggleDropdown={toggleDropdown}
                    closeMobile={closeMobile}
                  />
                );
              }
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default SideBar;